import { tracking } from '@dazn/bootstrap-lib';

import { installAPI } from './api/installAPI';
import { createAPIBuilder } from './lib/createAPIBuilder';
import { PublicAPIDeps } from './lib/PublicAPIDeps';

/**
 * Maps module return values to the exposed API contract on `window.dazn`. See {@link ./lib/PublicAPIDeps.ts}
 *
 * That API is considered frozen for any change and will not evolve.
 * All new features should be implemented in `@daz/bootstrap-api-runtime`
 * and exposed via `@dazn/bootstrap-api` modules.
 */
export const createPublicApiModule = ({
    deps,
    window,
    shouldTrack,
    shouldLog,
    logger = console.warn.bind(console),
}: {
    deps: PublicAPIDeps;
    window: Window & typeof globalThis;
    shouldTrack: boolean;
    shouldLog: boolean;
    logger?: (message: string) => void;
}) => {
    if (!shouldLog) {
        logger = () => null;
    }

    // already initialised
    if (window.dazn?.publicApiInitialised) {
        return;
    }

    const daznObject = window.dazn || {};

    Object.defineProperty(window, 'dazn', {
        enumerable: true,
        get() {
            return daznObject;
        },
        set(value) {
            if (value !== daznObject) {
                logger('You can not manipulate window.dazn directly');
            }
        },
    });

    const trackUsage = shouldTrack
        ? tracking.createTracking(tracking.createNRTracking(window), {
              defaultSampling: {
                  type: tracking.Sampling.Once,
              },
          })
        : null;

    const daznBridge = window.daznNativeBridge || {};

    Object.defineProperty(window, 'daznNativeBridge', {
        enumerable: true,
        get() {
            return daznBridge;
        },
        set(value) {
            if (value !== daznBridge) {
                logger('You can not manipulate window.daznBridge directly');
            }
        },
    });

    window.daznNativeBridge.messageToWeb = (
        eventName: string,
        payload: Record<string, string>,
    ) => {
        window.dazn.native.messageToWeb(eventName, payload);
    };

    const namespace = createAPIBuilder({
        window,
        trackUsage,
        logger,
        deps,
    });

    installAPI(namespace);
    Object.seal(window.dazn);
};
